import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import Image from 'next/image'
import 'swiper/css'
import 'swiper/css/pagination'
import 'swiper/css/navigation'
import styles from '../../../styles/common/features.module.css';
import styles2 from "../../../styles/UAEPageComponents.module.css";
import TalkToUsCustomButtonUAE from './TalkToUsCustomButtonUAE';
import VerificationPopupUAE from '@/components/HomeLanding/HomeLandingComponents/VerificationPopupUAE'

const getIsMobileViewport = () =>
  typeof window !== 'undefined' && window.innerWidth <= 600

const FeaturesSection = ({data, title}) => {
  const [showMore, setShowMore] = useState({ state: false, idx: null });
  const [visibleSections, setVisibleSections] = useState(new Set());
  const [animatedSections, setAnimatedSections] = useState(new Set());
  // Desktop-first and SSR-stable (never read window during useState init).
  const [mobileView, setmobileView] = useState(0);
  const [demoModal, setDemoModal] = useState(false);
  const [formHeading, setFormHeading] = useState("");

  const scrollRef = useRef();
  const sectionRefs = useRef([]);
  const imageStackRefs = useRef([]);
  const image1Refs = useRef([]);
  const image2Refs = useRef([]);
  const image3Refs = useRef([]);
  const trigger1Refs = useRef([]);
  const trigger2Refs = useRef([]);
  const trigger3Refs = useRef([]);
  const animatedImages1 = useRef(new Set());
  const animatedImages2 = useRef(new Set());
  const animatedImages3 = useRef(new Set());

 

  useEffect(() => {
    const scrollContainer = scrollRef.current;
    if (!scrollContainer) return;
    
    const handleScroll = () => {
      if (scrollContainer.scrollTop) {
        setShowMore({ state: false });
      }
    };
    
    scrollContainer.addEventListener("scroll", handleScroll);
    
    return () => {
      scrollContainer.removeEventListener("scroll", handleScroll);
    };
  }, []);
  const setWindowDimensions = () => {
    setmobileView(getIsMobileViewport() ? 1 : 0);
  };

  // useLayoutEffect runs before paint — avoids a one-frame mobile flash on desktop.
  useLayoutEffect(() => {
    setWindowDimensions();
    window.addEventListener("resize", setWindowDimensions);
    return () => {
      window.removeEventListener("resize", setWindowDimensions);
    };
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const sectionId = entry.target.getAttribute('data-section-id');
          if (entry.isIntersecting && !animatedSections.has(sectionId)) {
            setVisibleSections(prev => new Set([...prev, sectionId]));
            setTimeout(() => {
              setAnimatedSections(prev => new Set([...prev, sectionId]));
            }, 3000);
          }
        });
      }

    );

    sectionRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => {
      sectionRefs.current.forEach((ref) => {
        if (ref) observer.unobserve(ref);
      });
    };
  }, [data]);

  useEffect(() => {
    if (typeof window === 'undefined' || !data?.length) return;

    let cancelled = false;
    // Slightly later than before so floats don't start as soon as the section peeks in.
    const FLOAT_START = "top 75%";
    const START_DELAY = 0.25; // small pause before the first pop

    const killTriggers = (refs) => {
      refs.current.forEach((trigger) => {
        if (trigger?.kill) trigger.kill();
      });
      refs.current = [];
    };

    const initAnimations = async () => {
      const { gsap } = await import("gsap");
      const { ScrollTrigger } = await import("gsap/dist/ScrollTrigger");
      if (cancelled) return;
      gsap.registerPlugin(ScrollTrigger);

      // Wait for desktop/mobile image stack refs to mount after layout.
      await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
      if (cancelled) return;

      killTriggers(trigger1Refs);
      killTriggers(trigger2Refs);
      killTriggers(trigger3Refs);
      animatedImages1.current.clear();
      animatedImages2.current.clear();
      animatedImages3.current.clear();

      const playFloat = (el, width, height, { delay, duration }, imageKey, animatedSet) => {
        if (!el || animatedSet.has(imageKey)) return;
        animatedSet.add(imageKey);
        gsap.to(el, {
          width,
          height,
          duration,
          delay,
          overwrite: "auto",
        });
      };

      const attachFloatTrigger = ({
        el,
        triggerTarget,
        width,
        height,
        delay,
        duration,
        imageKey,
        animatedSet,
        triggerBag,
      }) => {
        if (!el || !triggerTarget || !width || !height) return;

        const play = () => playFloat(el, width, height, { delay, duration }, imageKey, animatedSet);

        const trigger = ScrollTrigger.create({
          trigger: triggerTarget,
          start: FLOAT_START,
          once: true,
          onEnter: play,
        });
        triggerBag.current.push(trigger);

        // Catch features already in view when triggers are created / after async load.
        if (trigger.progress > 0 || trigger.isActive) {
          play();
        } else {
          const rect = triggerTarget.getBoundingClientRect();
          if (rect.top < window.innerHeight * 0.75 && rect.bottom > 0) {
            play();
          }
        }
      };

      data.forEach((ele, idx) => {
        const triggerTarget = imageStackRefs.current[idx] || sectionRefs.current[idx];
        if (!triggerTarget) return;

        if (ele?.Image1) {
          attachFloatTrigger({
            el: image1Refs.current[idx],
            triggerTarget,
            width: ele.Image1.imageWidth,
            height: ele.Image1.imageHeight,
            delay: START_DELAY,
            duration: 0.1,
            imageKey: `image1-${idx}`,
            animatedSet: animatedImages1.current,
            triggerBag: trigger1Refs,
          });
        }

        if (ele?.Image2) {
          attachFloatTrigger({
            el: image2Refs.current[idx],
            triggerTarget,
            width: ele.Image2.imageWidth,
            height: ele.Image2.imageHeight,
            delay: START_DELAY + 0.2,
            duration: 0.1,
            imageKey: `image2-${idx}`,
            animatedSet: animatedImages2.current,
            triggerBag: trigger2Refs,
          });
        }

        if (ele?.Image3) {
          attachFloatTrigger({
            el: image3Refs.current[idx],
            triggerTarget,
            width: ele.Image3.imageWidth,
            height: ele.Image3.imageHeight,
            delay: START_DELAY,
            duration: 0.3,
            imageKey: `image3-${idx}`,
            animatedSet: animatedImages3.current,
            triggerBag: trigger3Refs,
          });
        }
      });

      ScrollTrigger.refresh();
    };

    initAnimations();

    return () => {
      cancelled = true;
      killTriggers(trigger1Refs);
      killTriggers(trigger2Refs);
      killTriggers(trigger3Refs);
    };
  }, [data, mobileView]);




  return (
    <>
      <div className={styles.featuresSection} style={{ width: '100%', maxWidth: '100%', fontFamily: 'GilroyMedium' }}>
        <VerificationPopupUAE isOpen={demoModal} closeModal={() => setDemoModal(false)} formHeading={formHeading} />

        <div ref={scrollRef} style={{ padding: mobileView ? "5rem 0rem " : "5rem 0" }}>

          {title && (
            <div style={{ maxWidth: '1850px', margin: '0 auto' }}>
              <div className={styles2.titleContainer}>
                <p style={{margin:0, padding:0, fontSize: '1.6rem', fontFamily: 'GilroySemiBold', lineHeight: '130%'}}>{title}</p>
              </div>
            </div>
          )}
          {
            data?.map((ele, idx) => {

              return (
                <React.Fragment key={ele.id || idx}>
                  <div
                    style={{
                      backgroundColor: ele?.bgColor ?? (idx % 2 !== 0 ? (idx === 1 ? '#161747' : '#0B5056') : 'transparent'),
                      width: '100%'
                    }}
                  >
                    <div style={{ maxWidth: '1850px', margin: '0 auto' }}>
                     <div
                      ref={(el) => sectionRefs.current[idx] = el}
                      data-section-id={ele.id}
                      className={styles.feature_container}
                      style={{ 
                        margin: mobileView ? " 2rem" : "0rem 18rem",
                        padding:  mobileView ? "3rem 1rem" : "10rem 0",
                        position: "relative",
                        flexDirection: "row",
                        width: 'auto',
                        ...(idx === 1 ? {
                          backgroundImage: "url('/assets/UAERetailPage/InventoryManagementFrame.svg')",
                          backgroundRepeat: "no-repeat",
                          backgroundPosition: "bottom center",
                          backgroundSize: "contain"
                        } : idx === 3 ? {
                          backgroundImage: "url('/assets/UAERetailPage/Reporting&AnalyticsFrame.svg')",
                          backgroundRepeat: "no-repeat",
                          backgroundPosition: "top center",
                          backgroundSize: "contain"
                        } : idx === 5 ? {
                          backgroundImage: "url('/assets/UAERetailPage/Reporting&AnalyticsFrame.svg')",
                          backgroundRepeat: "no-repeat",
                          backgroundPosition: "top center",
                          backgroundSize: "contain"
                        } :idx ===7 ? {
                          backgroundImage: "url('/assets/UAERetailPage/Reporting&AnalyticsFrame.svg')",
                          backgroundRepeat: "no-repeat",
                          backgroundPosition: "top center",
                          backgroundSize: "contain"
                        }:{})
                      }}
                    >
                    <div className={styles.left_section}>
                      {
                        ele?.icon &&
                        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', }}>
                          <div className={styles.icon}><Image src={ele?.icon} alt='icon' fill /></div>
                          <div className={styles.tag_text} style={{ 
                            color: ele?.tagColor ?? (idx === 4 ? '#2A6AB4' : idx === 2 ? '#016A5E' : (idx % 2 !== 0) ? 'white' : undefined),
                          }}>{ele?.tag}</div>
                        </div>
                      }
                      <h4 className={styles.feature_heading}
                       style={{ 
                        color: ele?.titleColor ?? (idx === 0 ? '#2A6AB4' :  (idx % 2 !== 0) ? 'white' : undefined),
                        fontFamily: 'bicyclette',
                        fontWeight: 'bold',
                        fontStyle: '700',
                        fontSize: '2.5rem',
                      }}>{ele?.title}</h4>
                      {
                        mobileView ?
                          <div className={styles.right_side} style={{ margin: "4rem 0 2rem 0" }}>
                            <div
                              ref={(el) => imageStackRefs.current[idx] = el}
                              className={styles2.stack_container}>
                              {ele?.Image1 && (
                                <div 
                                  ref={(el) => image1Refs.current[idx] = el}
                                  className={styles.stack_card_image} 
                                  style={{ height: 0, width: 0, bottom: ele?.Image1?.mobileBottom, right: ele?.Image1?.mobileRight, top: ele?.Image1?.mobileTop, left: ele?.Image1?.mobileLeft, zIndex: 5, opacity: 1, }}
                                >
                                  <Image src={ele?.Image1?.src} fill alt='image1' />
                                </div>
                              )}
                              {ele?.Image2 && (
                                <div 
                                  ref={(el) => image2Refs.current[idx] = el}
                                  className={styles.stack_card_image} 
                                  style={{ height: 0, width: 0, bottom: ele?.Image2?.mobileBottom, right: ele?.Image2?.mobileRight, top: ele?.Image2?.mobileTop, left: ele?.Image2?.mobileLeft, zIndex: 4, opacity: 1, }}
                                >
                                  <Image src={ele?.Image2?.src} fill alt='image2' />
                                </div>
                              )}
                              {ele?.Image3 && (
                                <div 
                                  ref={(el) => image3Refs.current[idx] = el}
                                  className={styles.stack_card_image} 
                                  style={{ height: 0, width: 0, bottom: ele?.Image3?.mobileBottom, right: ele?.Image3?.mobileRight, top: ele?.Image3?.mobileTop, left: ele?.Image3?.mobileLeft, zIndex: 3, opacity: 1, }}
                                >
                                  <Image src={ele?.Image3?.src} fill alt='image3' />
                                </div>
                              )}
                              {ele?.Image4 && (
                                <div className={styles.stack_card_image}
                                style={{ height: ele?.Image4?.mobileImageHeight, width: ele?.Image4?.mobileImageWidth, bottom: ele?.Image4?.mobileBottom, right: ele?.Image4?.mobileRight, top: ele?.Image4?.mobileTop, left: ele?.Image4?.mobileLeft, zIndex: 2, opacity: 1, backgroundColor: 'white', borderRadius: '8px', padding: '10px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}><Image src={ele?.Image4?.src} fill alt='image4' /></div>
                              )}

                              <div
                                className={styles2.stack_cardMobile}
                                style={{
                                  width: '100%',
                                  maxWidth: ele?.imageWidth,
                                  aspectRatio: ele?.imageWidth && ele?.imageHeight ? `${parseFloat(ele.imageWidth)}/${parseFloat(ele.imageHeight)}` : undefined,
                                  height: ele?.imageWidth && ele?.imageHeight ? undefined : ele?.imageHeight,
                                  bottom: ele?.imageBottom,
                                  right: ele?.imageRight,
                                  top: ele?.imageTop,
                                  left: ele?.imageLeft,
                                  zIndex: ele?.imageZIndex,
                                  opacity: ele?.imageOpacity,
                                }}
                              >
                                <Image src={ele?.image} alt={ele?.title} fill />
                              </div>
                            </div>
                          </div> : null 
                      }
                      <p className={styles.feature_body_text} style={{ color: (idx % 2 !== 0) ? 'white' : "#5a5a5a" }}>{ele?.desc}</p>
                      <div className={styles.readMoreParentDiv}>
                        {
                          ele?.points?.map((item, index) => {
                            return (
                              <div key={index} className={styles.feature_body_text}
                                style={{
                                  display: 'flex',
                                  alignItems: 'flex-start',
                                  marginTop: "1.5rem",
                                  gap: '1rem'
                                }}>
                                <span style={{
                                  width: '1.5rem',
                                  height: '1.5rem',
                                  borderRadius: '50%',
                                  backgroundColor: (idx % 2 !== 0) ? 'rgba(238, 237, 237, 0.35)' : ele?.dotOutlineColor || (idx === 2 || idx === 4 ? '#E6F7F5' : '#F0F9FF'),
                                  flexShrink: 0,
                                  marginTop: '0.25rem',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}>
                                  <span style={{
                                    width: '0.8rem',
                                    height: '0.8rem',
                                    borderRadius: '50%',
                                    backgroundColor: (idx % 2 !== 0) ? '#FFFFFF' : ele?.dotInnerColor || (idx === 2 ? '#016A5E' : '#2A6AB4')
                                  }}></span>
                                </span>
                                <span style={{ color: (idx % 2 !== 0) ? 'white' : undefined }}>{item}</span>
                              </div>
                            )
                          })
                        }
                      </div>
                      <TalkToUsCustomButtonUAE
                        text={ele?.btnText}
                        style={mobileView ?
                           {
                             margin: '1rem 0' ,
                             color: ele?.ctaColor,
                             fontFamily: 'bicyclette',
                             fontWeight: 'bold',
                             fontStyle: '700',
                             fontSize: '1.8rem',
                            } : 
                            {
                               marginTop: '3rem',
                               color: ele?.ctaColor,
                               fontFamily: 'bicyclette',
                               fontWeight: 'bold',
                               fontStyle: '700',
                               fontSize: '1.6rem',
                              }}
                        idx={idx}
                        onClick={() => {
                          setFormHeading(ele?.btnText || "We'll call you soon!");
                          setDemoModal(true);
                        }} />
                    </div>
                    {
                      !mobileView &&
                      <div className={styles.right_side}>
                        <div
                          ref={(el) => imageStackRefs.current[idx] = el}
                          className={styles.stack_container}>
                          {ele?.Image1 && (
                            <div 
                              ref={(el) => image1Refs.current[idx] = el}
                              className={styles.stack_card_image} 
                              style={{ height: 0, width: 0, bottom: ele?.Image1?.bottom, right: ele?.Image1?.right, top: ele?.Image1?.top, left: ele?.Image1?.left, zIndex: 5, opacity: 1 }}
                            >
                              <Image src={ele?.Image1?.src} fill alt='image1' />
                            </div>
                          )}
                          {ele?.Image2 && (
                            <div 
                              ref={(el) => image2Refs.current[idx] = el}
                              className={styles.stack_card_image} 
                              style={{ height: 0, width: 0, bottom: ele?.Image2?.bottom, right: ele?.Image2?.right, top: ele?.Image2?.top, left: ele?.Image2?.left, zIndex: 4, opacity: 1, }}
                            >
                              <Image src={ele?.Image2?.src} fill alt='image2' />
                            </div>
                          )}
                          {ele?.Image3 && (
                            <div 
                              ref={(el) => image3Refs.current[idx] = el}
                              className={styles.stack_card_image} 
                              style={{ height: 0, width: 0, bottom: ele?.Image3?.bottom, right: ele?.Image3?.right, top: ele?.Image3?.top, left: ele?.Image3?.left, zIndex: 3, opacity: 1, }}
                            >
                              <Image src={ele?.Image3?.src} fill alt='image3' />
                            </div>
                          )}
                          {ele?.Image4 && (
                            <div className={styles.stack_card_image} style={{ height: ele?.Image4?.imageHeight, width: ele?.Image4?.imageWidth, bottom: ele?.Image4?.bottom, right: ele?.Image4?.right, top: ele?.Image4?.top, left: ele?.Image4?.left, zIndex: 2, opacity: 1, }}><Image src={ele?.Image4?.src} fill alt='image4' /></div>
                          )}


                          <div className={styles.stack_card} style={{ height: ele?.imageHeight, width: mobileView ? "100%" : ele?.imageWidth, bottom: ele?.imageBottom ?? '2rem', right: ele?.imageRight ?? '2.5rem' }}><Image src={ele?.image} alt={ele?.title} fill /></div>
                        </div>
                      </div>
                    }
                    </div>
                    </div>
                  </div>

                </React.Fragment>
              )
            })
          }
        </div>
      </div>
    </>
  )
};

export default FeaturesSection;
